import React, { useState, useEffect } from "react";
import Loader from "../../components/Loader";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationPopup from "./ConfirmationPopup";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
import QuationDetails from "./QuationDetails";
const baseUrl = API_URL;

const OrdersDetails = () => {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const { order_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [preloading, setPreloading] = useState(true);
  const [preloadedFiles, setPreloadedFiles] = useState(new Map());
  const [orderDetails, setOrderDetails] = useState(null);
  const [confirmed, setConfirmed] = useState("pending");
  const [confirmedStatus, setConfirmedStatus] = useState(null);
  const [confirmedPopupIsOpen, setConfirmedPopupIsOpen] = useState(false);
  const [appliedPopupIsOpen, setAppliedPopupIsOpen] = useState(false);
  const [customOrder, setCustomOrder] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const [status, setStatus] = useState(["Shipped"]);
  const [statusFilter, setStatusFilter] = useState("shipped");

  const [fetchError, setFetchError] = useState(null);
  const { isAuthenticated, userRole } = useAuth();


  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchOrder = async () => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
        return;
      }

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/suppliers/orders/${order_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );

      if (response.status === 404) {
        navigate("/dashboard");
        throw new Error(t("ordersDetails.errors.updateFailed"));
      }

      if (response.status === 401) {
        navigate("/login");
        throw new Error(t("ordersDetails.errors.updateFailed"));
      }

      if (!response.ok) {
        throw new Error(t("ordersDetails.errors.updateFailed"));
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching order details:", error);
      return null;
    }
  };

  useEffect(() => {
    const getOrder = async () => {
      setLoading(true);
      const details = await fetchOrder();
      setOrderDetails(details);
      // if customized is true set is customOrder to true
      if (details?.order?.customized && details?.order?.total == 0) {
        setCustomOrder(true);
      }
      setConfirmed(details?.order?.status);
      setLoading(false);
    };
    getOrder();
  }, [order_id]);

  // Use shipping_price directly from the order response
  const shippingPriceValue =
    parseFloat(orderDetails?.order?.shipping_price) || 0;
  const shippingPrice = shippingPriceValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Format dates helper function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString(getLangFromLocalStorage(), {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC", // This will display the time in UTC
    });
  };

  const formatRentalPeriod = (rentalStart, rentalEnd) => {
    if (!rentalStart || !rentalEnd) return "-";
    return `${formatDate(rentalStart)} → ${formatDate(rentalEnd)}`;
  };

  const supplierServices = orderDetails?.order?.additions?.services || [];
  const hasRentalServices = supplierServices.some(
    (service) => service.offer_type === "rent",
  );
  const supplierTableColSpan = hasRentalServices ? 6 : 5;

  const handleConfirmationPopup = (status) => {
    setConfirmedStatus(status);
    setConfirmedPopupIsOpen(true);
  };

  const handleApply = async () => {
    if (!statusFilter) {
      setErrorMessages({ message: t("ordersDetails.errors.chooseStatus") });
      return;
    }
    const response = await handleAction(statusFilter);

    if (
      response?.ok &&
      Array.isArray(errorMessages) &&
      errorMessages.length === 0
    ) {
      setAppliedPopupIsOpen(true);
      setTimeout(() => {
        setAppliedPopupIsOpen(false);
        navigate(-1);
      }, 1500);
    }
  };

  const handleAction = async (status) => {
    setIsSubmitting(true);
    setErrorMessages([]);

    try {
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }

      if (status === "confirmed") {
        status = "accepted";
      }

      const orderStatus = { order_id, status };
      const response = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/suppliers/respondOrder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
          body: JSON.stringify(orderStatus),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error(t("ordersDetails.errors.updateFailed"));
        }

        const errorData = await response.json();

        if (errorData.errors) {
          const backendErrors = errorData.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
          setErrorMessages(backendErrors);
        } else if (errorData.error) {
          setErrorMessages({ message: errorData.error });
        } else {
          setErrorMessages({
            message: t("ordersDetails.errors.updateFailed"),
          });
        }
        throw new Error(t("ordersDetails.errors.updateFailed"));
      }

      const data = await response.json();
      console.log("Order status updated successfully:", data);
      setConfirmed(status);
      if (status === "rejected" && !statusFilter) {
        navigate(-1);
      }
      setConfirmedPopupIsOpen(false);
      setErrorMessages([]);
      return response;
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  //POST /api/v1/attnd-supplier/suppliers/respondCustomQuote
  const handleDeclineFunction = async () => {
    setIsSubmitting(true);
    setErrorMessages([]);

    try {
      const response = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/suppliers/respondCustomQuote`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
          body: JSON.stringify({
            order_id: parseInt(order_id),
            status: "rejected"
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to decline request");
      }

      // Update order status locally
      setOrderDetails(prev => ({
        ...prev,
        order: {
          ...prev.order,
          status: "rejected"
        }
      }));

      console.log("Request declined successfully");
    } catch (error) {
      console.error("Error declining request:", error);
      setErrorMessages([error.message || "Failed to decline request"]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendQuoteFunction = async (formData) => {
    // Validate required fields
    const errors = [];

    if (!formData.estimatedCost || formData.estimatedCost.trim() === '') {
      errors.push("Estimated cost is required");
    } else if (isNaN(parseFloat(formData.estimatedCost)) || parseFloat(formData.estimatedCost) <= 0) {
      errors.push("Estimated cost must be a valid positive number");
    }

    if (!formData.deliveryTime || formData.deliveryTime.trim() === '') {
      errors.push("Estimated delivery time is required");
    } else if (isNaN(parseInt(formData.deliveryTime)) || parseInt(formData.deliveryTime) <= 0) {
      errors.push("Delivery time must be a valid positive number");
    }

    // Additional validation for shipping period to ensure it's positive
    if (formData.deliveryTime && !isNaN(parseInt(formData.deliveryTime))) {
      const shippingPeriod = parseInt(formData.deliveryTime);
      if (shippingPeriod <= 0) {
        errors.push("Shipping period must be a positive number greater than 0");
      }
    }

    if (errors.length > 0) {
      setErrorMessages(errors);
      console.error("Validation errors:", errors);
      return;
    }

    setIsSubmitting(true);
    setErrorMessages([]);

    try {
      const body = new FormData();
      body.append("order_id", String(parseInt(order_id, 10)));
      body.append("status", "accepted");
      body.append("price", String(parseFloat(formData.estimatedCost)));
      body.append("shipping_period", String(formData.deliveryTime));
      body.append("comments", formData.additionalComments || "");
      if (formData.quoteFile) {
        body.append("quote_file", formData.quoteFile);
      }

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/suppliers/respondCustomQuote`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            lang: getLangFromLocalStorage(),
          },
          body,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error Response:", errorData);

        // Display the raw server error message directly to user
        throw new Error(errorData.message || errorData.error || "Failed to send quote");
      }

      // Refresh order details so quote_file_url and hydrated fields appear
      const refreshed = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/suppliers/orders/${order_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            lang: getLangFromLocalStorage(),
          },
        }
      );
      if (refreshed.ok) {
        const refreshedData = await refreshed.json();
        setOrderDetails(refreshedData.data || refreshedData);
      } else {
        setOrderDetails((prev) => ({
          ...prev,
          order: {
            ...prev.order,
            status: "accepted",
          },
        }));
      }

      console.log("Quote sent successfully");
    } catch (error) {
      console.error("Error sending quote:", error);

      // Display the raw server error message to see exactly what the API returns
      setErrorMessages([error.message || "Failed to send quote"]);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  } else {
    return (
      <div className="order-details">
        <div className="details-header">
          {/* Title with Message Button on the same line */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2 className="order-details-heading font-[800]">
              {t("ordersDetails.heading")}
            </h2>
            {/* Message Button - only show if user can access chat */}
            {userRole !== 'viewer' && (
              <button
                className="message-btn"
                onClick={() =>
                  navigate("/messages", {
                    state: {
                      orderId: order_id,
                    },
                  })
                }
                style={{
                  padding: "12px",
                  backgroundColor: "#5007C5",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#5007C5")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#5007C5")}
              >
                {t("ordersDetails.buttons.message")}
                <svg
                  width="18"
                  height="19"
                  viewBox="0 0 18 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.5 6.07227V10.5723C16.5 11.6973 16.125 12.6348 15.465 13.2948C14.8125 13.9473 13.875 14.3223 12.75 14.3223V15.9198C12.75 16.5198 12.0825 16.8798 11.5875 16.5498L8.25 14.3223H6.66C6.72 14.0973 6.75 13.8648 6.75 13.6248C6.75 12.8598 6.4575 12.1548 5.9775 11.6223C5.4375 11.0073 4.635 10.6248 3.75 10.6248C2.91 10.6248 2.145 10.9698 1.5975 11.5323C1.53 11.2323 1.5 10.9098 1.5 10.5723V6.07227C1.5 3.82227 3 2.32227 5.25 2.32227H12.75C15 2.32227 16.5 3.82227 16.5 6.07227Z"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.75 13.625C6.75 13.865 6.72 14.0975 6.66 14.3225C6.5925 14.6225 6.47251 14.915 6.31501 15.17C5.79751 16.04 4.845 16.625 3.75 16.625C2.9775 16.625 2.28 16.3325 1.755 15.8525C1.53 15.6575 1.33499 15.425 1.18499 15.17C0.90749 14.72 0.75 14.1875 0.75 13.625C0.75 12.815 1.0725 12.0725 1.5975 11.5325C2.145 10.97 2.91 10.625 3.75 10.625C4.635 10.625 5.4375 11.0075 5.9775 11.6225C6.4575 12.155 6.75 12.86 6.75 13.625Z"
                    fill="white"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.86731 13.6094H2.63232"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3.75 12.5146V14.7571"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.375 8.375H11.625"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Order Number with Samples Indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <h3 className="details-heading font-semibold text-[20px]">
              {t("ordersDetails.orderNumberPrefix")}
              {orderDetails?.order?.id}
            </h3>
            {orderDetails?.order?.additions?.services?.some(
              (service) => service.sample_product
            ) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px",
                  gap: "8px",
                  width: "149px",
                  height: "36px",
                  background: "rgba(85, 217, 135, 0.18)",
                  borderRadius: "24px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#2E7D32",
                }}
              >
                {t("ordersDetails.samples")}
              </div>
            )}
          </div>
          {/* <div className="flex align-center gap-x-[220px] gap-y-[24px] flex-wrap">
            <h4 className="text-[16px] font-normal leading-[19.36px]">
              {t("ordersDetails.labels.customer")}:{" "}
              <span className="font-[600]">
                {orderDetails?.customer_details?.customer_name}
              </span>
            </h4>
            <h4 className="text-[16px] font-normal leading-[19.36px]">
              {t("ordersDetails.labels.address")}:{" "}
              <span className="font-[600]">
                {orderDetails?.customer_details?.address}
              </span>
            </h4>


          </div> */}

          {/* Order Data and Samples Section - positioned like second image */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              marginTop: "24px",
              marginBottom: "24px",
              flexWrap: "wrap",
            }}
          >
            {/* Order Dates */}
            <div
              className="order-data"
              style={{
                // backgroundColor: "#f8f9fa",
                padding: "16px",
                borderRadius: "8px",
                // border: "1px solid #e9ecef",
                maxWidth: "672px",
                display: "flex",
                justifyContent: "space-between",
                flex: "1",
              }}
            >
              <h4 className="data-label" style={{ marginBottom: "12px" }}>
                {t("ordersDetails.labels.date")}:{" "}
                <span className="details-date">
                  <img src="/calendar2.svg" style={{ marginRight: "6px" }} />
                  {formatDate(orderDetails?.order?.deliveryDate)}
                </span>
              </h4>

              {/* {orderDetails?.order?.start_date && (
                <h4 className="data-label" style={{ marginBottom: "12px" }}>
                  {t("ordersDetails.labels.startDate")}:{" "}
                  <span className="details-date">
                    <img src="/calendar2.svg" style={{ marginRight: "6px" }} />
                    {formatDate(orderDetails?.order?.start_date)}
                  </span>
                </h4>
              )}
              {orderDetails?.order?.end_date && (
                <h4 className="data-label" style={{ marginBottom: "12px" }}>
                  {t("ordersDetails.labels.endDate")}:{" "}
                  <span className="details-date">
                    <img src="/calendar2.svg" style={{ marginRight: "6px" }} />
                    {formatDate(orderDetails?.order?.end_date)}
                  </span>
                </h4>
              )} */}
              {/* customer */}
              {orderDetails?.customer_details?.customer_name && (
                <h4 className="data-label" style={{ marginBottom: "12px" }}>
                  {t("ordersDetails.labels.customer")}:{" "}
                  <span className="details-date">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.99967 7.50033C11.6105 7.50033 12.9163 6.19449 12.9163 4.58366C12.9163 2.97283 11.6105 1.66699 9.99967 1.66699C8.38884 1.66699 7.08301 2.97283 7.08301 4.58366C7.08301 6.19449 8.38884 7.50033 9.99967 7.50033Z"
                        stroke="#6609F8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M1.6665 17.0837C1.6665 13.4016 5.02442 10.417 9.1665 10.417M12.9165 17.5003L17.0832 13.3337L15.4165 11.667L11.2498 15.8337V17.5003H12.9165Z"
                        stroke="#6609F8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    {orderDetails?.customer_details?.customer_name || "-"}
                  </span>
                </h4>
              )}
              {/* location */}
              {orderDetails?.order?.location && (
                <h4 className="data-label" style={{ marginBottom: "12px" }}>
                  {t("ordersDetails.labels.address")}:{" "}
                  <span className="details-date">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.9999 11.1912C11.4358 11.1912 12.5999 10.0272 12.5999 8.59121C12.5999 7.15527 11.4358 5.99121 9.9999 5.99121C8.56396 5.99121 7.3999 7.15527 7.3999 8.59121C7.3999 10.0272 8.56396 11.1912 9.9999 11.1912Z"
                        stroke="#6609F8"
                        stroke-width="1.5"
                      />
                      <path
                        d="M3.01675 7.07533C4.65842 -0.141339 15.3501 -0.133006 16.9834 7.08366C17.9417 11.317 15.3084 14.9003 13.0001 17.117C11.3251 18.7337 8.67508 18.7337 6.99175 17.117C4.69175 14.9003 2.05842 11.3087 3.01675 7.07533Z"
                        stroke="#6609F8"
                        stroke-width="1.5"
                      />
                    </svg>
                    {orderDetails?.order?.location || "-"}
                  </span>
                </h4>
              )}
              {(orderDetails?.order?.shipping_city ||
                orderDetails?.order?.shipping_city_name) && (
                <h4 className="data-label" style={{ marginBottom: "12px" }}>
                  {t("ordersDetails.labels.shippingCity")}:{" "}
                  <span className="details-date">
                    {orderDetails?.order?.shipping_city ||
                      orderDetails?.order?.shipping_city_name ||
                      "-"}
                  </span>
                </h4>
              )}
            </div>
          </div>
        </div>
        {customOrder ? (
          <QuationDetails
            orderDetails={orderDetails}
            onDecline={handleDeclineFunction}
            onSendQuote={handleSendQuoteFunction}
            isSubmitting={isSubmitting}
            errorMessages={errorMessages}
          />
        ) : (
          <div className="financial-details">
            <div className="flex justify-between items-center mb-[16px]">
              <h3 className="financial-heading">
                {t("ordersDetails.financialHeading")}
              </h3>
              {(confirmed === "accepted" || confirmed === "delivered") && (
                <div className="confirmed-badge">
                  {t("ordersDetails.statusBadges.confirmed")}
                </div>
              )}
              {confirmed === "rejected" && (
                <div className="rejected-badge">
                  {t("ordersDetails.statusBadges.rejected")}
                </div>
              )}
              {confirmed === "shipped" && (
                <div className="status-details-badge">
                  {t("ordersDetails.statusBadges.shipped")}
                </div>
              )}
              {confirmed === "paid" && (
                <div className="confirmed-badge">
                  {t("ordersDetails.statusBadges.paid")}
                </div>
              )}
              {confirmed === "pending" && (
                <div className="pending-badge">
                  {t("ordersDetails.statusBadges.pending")}
                </div>
              )}
              {confirmed === "no_responded" && (
                <div className="pending-badge">
                  {t("ordersDetails.statusBadges.noResponded")}
                </div>
              )}
              {/* canceled */}
              {confirmed === "cancelled" && (
                <div className="rejected-badge">
                  {t("ordersDetails.statusBadges.cancelled")}
                </div>
              )}
            </div>
            <div className="order-table table-wrapper">
              <table className="orders-table details">
                <thead>
                  <tr>
                    <th className="text-left">
                      {t("ordersDetails.table.headers.product")}
                    </th>
                    <th>{t("ordersDetails.table.headers.sku")}</th>
                    <th>{t("ordersDetails.table.headers.note")}</th>
                    {hasRentalServices && (
                      <th>{t("ordersDetails.table.headers.rentalPeriod")}</th>
                    )}
                    <th>{t("ordersDetails.table.headers.quantity")}</th>
                    <th className="total-cost">
                      {t("ordersDetails.table.headers.cost")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails?.order?.additions?.services?.length > 0 ? (
                    orderDetails?.order?.additions?.services?.map(
                      (order, index) => (
                        <tr key={index}>
                          <td className="product-cell">
                            <div className="product-info">
                              {/* Product Image */}
                              {order?.file && (
                                <div className="product-image">
                                  <img
                                    src={order?.file}
                                    alt={order.name || "Service File"}
                                    className="product-img"
                                  />
                                </div>
                              )}

                              {/* Product Name */}
                              <div className="product-details">
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <h4 className="product-name">
                                    {order.name || "Unknown"}
                                  </h4>
                                  {/* {order.sample_product && (
                                  <span
                                    style={{
                                      backgroundColor: "#e3f2fd",
                                      color: "#1976d2",
                                      fontSize: "10px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {t("ordersDetails.table.sample")}
                                  </span>
                                )} */}
                                </div>

                                {/* Download Icon */}
                                {order?.file && (
                                  <a
                                    href={order?.file}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="download-link"
                                  >
                                    <img
                                      src="/receive-square.svg"
                                      alt={t(
                                        "ordersDetails.statusBadges.confirmed"
                                      )}
                                      className="download-icon"
                                    />
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{order.sku || "-"}</td>
                          <td>{order?.notes || "-"}</td>
                          {hasRentalServices && (
                            <td>
                              {order.offer_type === "rent"
                                ? formatRentalPeriod(
                                    order.rental_start,
                                    order.rental_end,
                                  )
                                : "-"}
                            </td>
                          )}
                          <td>{order?.quantity || "-"}</td>
                          <td className="total-cost">
                            {order?.price || "-"}{" "}
                            {t("ordersDetails.totals.sar")}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan={supplierTableColSpan} className="no-data">
                        {t("ordersDetails.table.noProducts")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Totals Section */}
              <div className="totals-section">
                <div className="totals-row">
                  <span className="total-label">
                    {t("ordersDetails.totals.subTotal")}
                  </span>
                  <span className="total-value">
                    {orderDetails?.order?.sub_total}{" "}
                    {t("ordersDetails.totals.sar")}
                  </span>
                </div>

                <div className="totals-row">
                  <span className="total-label">
                    {t("ordersDetails.totals.shipping")}
                  </span>
                  <span className="total-value">
                    {shippingPrice} {t("ordersDetails.totals.sar")}
                  </span>
                </div>

                <div className="totals-row">
                <span className="total-label">
                  {t("ordersDetails.totals.attndFees")}
                </span>
                <span className="total-value">
                  {Number(orderDetails?.order?.attnd_fees).toLocaleString()}{" "}
                  {t("ordersDetails.totals.sar")}
                </span>
              </div>

                <div className="totals-row total-vat">
                  <span className="total-label">
                    {t("ordersDetails.totals.vat")}
                  </span>
                  <span className="total-value">
                    {Number(orderDetails?.order?.tax).toLocaleString()}{" "}
                    {t("ordersDetails.totals.sar")}
                  </span>
                </div>

                <div className="totals-row total-final">
                  <span className="total-label">
                    {t("ordersDetails.totals.total")}
                  </span>
                  <span className="total-value">
                    {Number(orderDetails?.order?.total).toLocaleString()}{" "}
                    {t("ordersDetails.totals.sar")}
                  </span>
                </div>
              </div>

              {errorMessages && (
                <p
                  className="mt-4 error-messages"
                  style={{ marginBottom: "15px" }}
                >
                  {errorMessages.message}
                </p>
              )}
            </div>

            <div className="order-actions-btns">
              {userRole !== 'viewer' && confirmed === "pending" && (
                <>
                  <button
                    className="reject-btn"
                    onClick={() => handleConfirmationPopup("rejected")}
                    disabled={isSubmitting}
                  >
                    {t("ordersDetails.buttons.decline")}
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={() => handleConfirmationPopup("accepted")}
                    disabled={isSubmitting}
                  >
                    {t("ordersDetails.buttons.confirm")}
                  </button>
                </>
              )}
              {userRole !== 'viewer' && confirmed === "paid" && (
                <>
                  <button
                    className="apply-btn"
                    onClick={handleApply}
                    disabled={isSubmitting}
                  >
                    {t("ordersDetails.buttons.apply")}
                  </button>
                  <div className="status-select">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      {status.map((statusName, index) => (
                        <option key={index} value={statusName.toLowerCase()}>
                          {t(
                            `ordersDetails.statusBadges.${
                              statusName.toLowerCase() === "accepted"
                                ? "confirmed"
                                : statusName.toLowerCase()
                            }`
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {confirmedPopupIsOpen && (
          <ConfirmationPopup
            setConfirmedPopupIsOpen={setConfirmedPopupIsOpen}
            handleAction={handleAction}
            status={confirmedStatus}
            isSubmitting={isSubmitting}
          />
        )}
        {appliedPopupIsOpen && (
          <div className="apply-popup">
            <div className="apply-popup-content">
              <img
                src="/success.svg"
                alt={t("ordersDetails.applyPopup.message")}
              />
              <h4 className="apply-message">
                {t("ordersDetails.applyPopup.message")}
              </h4>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default OrdersDetails;
